import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "../utils/apiError";
import {
  HiOutlinePlus,
  HiOutlineExclamation,
  HiOutlineChevronLeft,
  HiOutlineCollection,
  HiOutlineSearch,
  HiOutlineX,
} from "react-icons/hi";
import {
  fetchCompetencyCategories,
  fetchCompetencyCategoryById,
  createCompetencyCategory,
  updateCompetencyCategory,
  deleteCompetencyCategory,
} from "../services/masterDataApi";
import Toast from "../components/Toast";
import Pagination from "../components/Pagination";
import { useUserRole } from "../hooks/useUserRole";
import { useDebounce } from "../hooks/useDebounce";

const EMPTY_FORM = {
  name: "",
  weight: "",
  description: "",
};

// A native type="number" input still lets you type a bare "e"/"E" (valid
// scientific-notation syntax on its own, even with no digits after it), so
// switching to type="text" plus this check catches it with a proper message
// instead of silently treating it as an empty/invalid number.
function getWeightError(raw) {
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return "Weight must be a valid number";
  return null;
}

/* ═══════════════════════════════════════════════════════════════════
   COMPETENCY CATEGORY PAGE
   ═══════════════════════════════════════════════════════════════════ */
function CompetencyCategory() {
  const { canManageCompetencies } = useUserRole();
  const [toast, setToast] = useState(null);

  // ── view state ── "list" | "form" — backed by the URL so a refresh in
  // edit mode reopens the same category instead of dropping back to the list
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "list";
  const urlEditId = searchParams.get("edit");
  const setView = useCallback((v, params = {}) => {
    if (v === "list") {
      setSearchParams({});
    } else if (v === "form" && params.edit) {
      setSearchParams({ view: "form", edit: params.edit });
    } else {
      setSearchParams({ view: v });
    }
  }, [setSearchParams]);

  // ── list ──
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  // ── pagination state ──
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // ── form ──
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── delete confirm ──
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  /* ── loaders ───────────────────────────────────────────────────── */

  const loadCategories = useCallback(async (page = currentPage, searchQuery = debouncedSearch) => {
    setLoading(true);
    try {
      const res = await fetchCompetencyCategories({ page, page_size: pageSize, search: searchQuery });
      setCategories(res.data || []);
      setTotalCount(res.total_count || 0);
      setTotalPages(res.total_pages || 1);
      setCurrentPage(res.current_page || page);
    } catch (err) {
      console.error("Failed to load categories", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    loadCategories(1, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, debouncedSearch]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  /* ── form helpers ──────────────────────────────────────────────── */

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setView("form");
  };

  const openEdit = (cat) => {
    // A direct click already has the full item — mark the URL-restore effect
    // as satisfied so it doesn't also redundantly re-fetch this same item.
    hasRestoredFromUrl.current = true;
    setForm({
      name: cat.name,
      weight: cat.weight != null ? String(cat.weight) : "",
      description: cat.description || "",
    });
    setEditingId(cat.id);
    setView("form", { edit: cat.id });
  };

  // Restore edit view from URL on refresh.
  const hasRestoredFromUrl = useRef(false);
  useEffect(() => {
    if (hasRestoredFromUrl.current) return;
    if (view === "form" && urlEditId) {
      hasRestoredFromUrl.current = true;
      fetchCompetencyCategoryById(urlEditId)
        .then((res) => openEdit(res))
        .catch(() => setView("list"));
    } else if (view === "form" && !urlEditId) {
      hasRestoredFromUrl.current = true;
      openCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, urlEditId]);

  const closeForm = () => {
    setView("list");
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setToast({ type: "error", message: "Category name is required" });
      return;
    }
    if (!/\p{L}/u.test(form.name.trim())) {
      setToast({ type: "error", message: "Name must contain at least one letter (not just numbers, spaces, or symbols)" });
      return;
    }
    const weightError = getWeightError(form.weight);
    if (weightError) {
      setToast({ type: "error", message: weightError });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        weight: form.weight !== "" ? Number.parseFloat(form.weight) : null,
        description: form.description || "",
      };
      if (editingId) {
        await updateCompetencyCategory(editingId, payload);
        setToast({ type: "success", message: "Category updated" });
        closeForm();
      } else {
        await createCompetencyCategory(payload);
        setToast({ type: "success", message: "Category created" });
        closeForm();
      }
      loadCategories();
    } catch (err) {
      setToast({
        type: "error",
        message: apiErrorMessage(err, "Failed to save category"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (catId) => {
    try {
      await deleteCompetencyCategory(catId);
      setShowDeleteConfirm(null);
      setToast({ type: "success", message: "Category deleted" });
      loadCategories();
      if (view !== "list") setView("list");
    } catch (err) {
      setToast({
        type: "error",
        message: err.response?.data?.detail || "Failed to delete category",
      });
    }
  };

  /* ── table content ─────────────────────────────────────────────── */

  const renderTableContent = () => {
    if (loading) {
      return (
        <div className="p-8 space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-4 animate-pulse">
              <div className="w-8 h-4 bg-[#e5e7eb] rounded" />
              <div className="flex-1 h-4 bg-[#e5e7eb] rounded" />
              <div className="w-16 h-4 bg-[#e5e7eb] rounded" />
              <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
            </div>
          ))}
        </div>
      );
    }

    if (categories.length === 0) {
      return (
        <div className="text-center py-16 px-5">
          <div className="w-16 h-16 bg-[#f3f4f6] rounded-lg flex items-center justify-center mx-auto mb-4">
            <HiOutlineCollection className="text-2xl text-[#8b949e]" />
          </div>
          <p className="m-0 text-sm font-medium text-[#57606a]">No categories found.</p>
          <p className="m-0 text-xs text-[#8b949e] mt-1">
            Create your first competency category to get started.
          </p>
        </div>
      );
    }

    return (
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
            <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a] w-[60px]">#</th>
            <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Name</th>
            <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a] w-[120px]">Weight</th>
            <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#e5e7eb]">
          {categories.map((cat, idx) => (
            <tr
              key={cat.id}
              onClick={() => openEdit(cat)}
              className="group transition-colors duration-150 hover:bg-gray-50 cursor-pointer"
            >
              <td className="py-4 px-6 text-[#8b949e] font-medium text-xs">{idx + 1}</td>
              <td className="py-4 px-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#eff6ff] rounded-md flex items-center justify-center flex-shrink-0">
                    <HiOutlineCollection className="text-sm text-[#0078d4]" />
                  </div>
                  <span className="font-medium text-[#24292f]">{cat.name}</span>
                </div>
              </td>
              <td className="py-4 px-6 text-[#57606a]">
                {cat.weight != null ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#eff6ff] text-[#0078d4]">
                    {cat.weight}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="py-4 px-6 text-[#57606a]">{cat.description || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  /* ═══════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════ */
  return (
    <div className="p-6">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ═══ LIST VIEW ═══ */}
      {view === "list" && (
        <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
          {/* ── Toolbar ── (title/subtitle come from the shared page Header) */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
            <div className="relative flex items-center flex-1 max-w-sm">
              <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
              <input
                type="text"
                className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
                placeholder="Search categories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
                  onClick={() => setSearch("")}
                >
                  <HiOutlineX className="text-sm" />
                </button>
              )}
            </div>
            {canManageCompetencies && (
              <button
                className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
                onClick={openCreate}
              >
                <HiOutlinePlus className="text-base" />
                Add Category
              </button>
            )}
          </div>

          {/* ── Table ── */}
          {renderTableContent()}

          {/* ── Pagination ── */}
          {!loading && totalCount > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={(p) => loadCategories(p)}
              onPageSizeChange={handlePageSizeChange}
              entityName="categories"
            />
          )}
        </div>
      )}

      {/* ═══ CREATE / EDIT FORM ═══ */}
      {view === "form" && (
        <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl border border-[#e5e7eb] w-full">
          {/* Form Header */}
          <div className="sticky top-14 z-40 flex items-center justify-between px-6 py-5 border-b border-[#e5e7eb] bg-white rounded-t-xl">
            <div className="flex items-center gap-3">
              <button
                onClick={closeForm}
                className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
                title="Back"
              >
                <HiOutlineChevronLeft className="text-lg" />
              </button>
              <div>
                <h3 className="m-0 text-[15px] font-semibold text-[#24292f]">
                  {!canManageCompetencies ? "Category Details" : editingId ? "Edit Category" : "Add New Category"}
                </h3>
                <p className="m-0 mt-1 text-[13px] text-[#8b949e]">
                  {!canManageCompetencies ? "View category information" : editingId ? "Update category information" : "Create a new competency category"}
                </p>
              </div>
            </div>
            {canManageCompetencies && editingId && (
              <button onClick={() => setShowDeleteConfirm({ id: editingId, name: form.name })} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">Delete</button>
            )}
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="cat-name" className="block mb-2 text-sm font-medium text-[#24292f]">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="cat-name"
                  className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
                  type="text"
                  placeholder="Enter category name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={!canManageCompetencies}
                  required
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="cat-weight" className="block mb-2 text-sm font-medium text-[#24292f]">
                  Weight
                </label>
                <input
                  id="cat-weight"
                  className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 1.0"
                  value={form.weight}
                  onChange={(e) => setForm({ ...form, weight: e.target.value })}
                  disabled={!canManageCompetencies}
                />
                {getWeightError(form.weight) && (
                  <p className="m-0 mt-1 text-xs text-red-500">{getWeightError(form.weight)}</p>
                )}
              </div>
              <div>
                <label htmlFor="cat-desc" className="block mb-2 text-sm font-medium text-[#24292f]">
                  Description
                </label>
                <input
                  id="cat-desc"
                  className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
                  type="text"
                  placeholder="Enter description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  disabled={!canManageCompetencies}
                />
              </div>
            </div>

            {/* Form Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-xl">
              <button
                type="button"
                className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
                onClick={closeForm}
              >
                {canManageCompetencies ? "Cancel" : "Back"}
              </button>
              {canManageCompetencies && (
                <button
                  type="submit"
                  disabled={saving || !form.name.trim()}
                  className="py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving…" : editingId ? "Save Changes" : "Create Category"}
                </button>
              )}
            </div>
          </form>
        </div>
        </div>
      )}

      {/* ═══ DELETE CONFIRM MODAL ═══ */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[250] p-4"
          onClick={() => setShowDeleteConfirm(null)}
        >
          <div
            className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-sm shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === "Escape") setShowDeleteConfirm(null); }}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <HiOutlineExclamation className="text-2xl text-red-600" />
              </div>
              <h3 className="m-0 text-lg font-semibold text-[#24292f]">Delete Category?</h3>
              <p className="m-0 mt-2 text-sm text-[#57606a] leading-relaxed">
                Are you sure you want to delete{" "}
                <span className="font-bold text-[#24292f]">{showDeleteConfirm.name}</span>?
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
              <button
                className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-red-600 text-white hover:bg-red-700 shadow-sm"
                onClick={() => handleDelete(showDeleteConfirm.id)}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompetencyCategory;
